import { useEffect, useState } from "react";
import { MapPin, Facebook } from "lucide-react";
import { IMAGES, Grain } from "./shared";

const DEFAULTS = {
  address: "Address To Be Determined",
  address_note: "Fully step-free entrance planned",
  hours: "To Be Determined",
  phone: "315-374-8156",
  facebook_url: "https://facebook-page-6.emergent.host",
};

export const Visit = () => {
  const [content, setContent] = useState(DEFAULTS);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setContent({ ...DEFAULTS, ...data }))
      .catch(() => {});
  }, []);

  const phoneHref = `tel:+1${content.phone.replace(/\D/g, "")}`;
  const hasRealAddress =
    content.address && !/to be determined/i.test(content.address);
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(content.address)}&output=embed`;

  return (
  <section
    id="visit"
    data-testid="visit-section"
    className="py-20 md:py-28 px-6 md:px-12 max-w-6xl mx-auto border-t border-[#3B2760]"
  >
    <div className="grid md:grid-cols-12 gap-8 items-start">
      <div className="md:col-span-5">
        <span className="mono-lbl text-xs text-[#FF2E88] uppercase font-bold">Plan Your Visit</span>
        <h2 className="display-type text-4xl md:text-5xl font-black uppercase mt-2 mb-6">
          Come Get Some Coffee and Play Some Games
        </h2>
        <div className="space-y-6 text-sm">
          <div data-testid="visit-address">
            <p className="font-bold uppercase text-xs mono-lbl text-[#A08FB8]">Address</p>
            <p className="text-base font-semibold mt-1">{content.address}</p>
            <p className="text-xs text-[#A08FB8] mt-0.5">{content.address_note}</p>
          </div>
          <div data-testid="visit-phone">
            <p className="font-bold uppercase text-xs mono-lbl text-[#A08FB8]">Questions?</p>
            <p className="text-base font-semibold mt-1">
              Call if you have any question:{" "}
              <a href={phoneHref} className="text-[#FF2E88] hover:underline">
                {content.phone}
              </a>
            </p>
          </div>
          <div data-testid="visit-social">
            <p className="font-bold uppercase text-xs mono-lbl text-[#A08FB8]">Follow Us</p>
            <a
              data-testid="visit-facebook-link"
              href={content.facebook_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-2 px-4 py-2.5 rounded-xl bg-[#FF2E88] text-white font-bold text-sm uppercase mono-lbl hover:bg-[#D61F73] transition-colors"
            >
              <Facebook className="w-4 h-4" />
              Facebook
            </a>
          </div>
          <div data-testid="visit-hours">
            <p className="font-bold uppercase text-xs mono-lbl text-[#A08FB8]">Opening Hours</p>
            <p className="mt-1 font-medium whitespace-pre-line">{content.hours}</p>
          </div>
        </div>
      </div>

      <div
        data-testid="map-frame"
        className="md:col-span-7 rounded-2xl overflow-hidden aspect-[16/10] relative flex items-center justify-center p-8 text-center text-white border border-[#3B2760]"
      >
        {hasRealAddress ? (
          <iframe
            data-testid="google-map-embed"
            title="Map to Amanda's Coffee & Retro Video Games"
            src={mapSrc}
            className="absolute inset-0 w-full h-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        ) : (
          <>
            <img
              src={IMAGES.neighborhoodMap}
              alt="Illustrated warm map showing cafe location near transit"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-[#1f140c]/70" />
            <Grain id="g-map" />
            <div className="relative z-10 max-w-sm">
              <div className="w-12 h-12 rounded-full bg-[#FF2E88] text-white flex items-center justify-center mx-auto mb-3 shadow-lg">
                <MapPin className="w-6 h-6" />
              </div>
              <h4 className="font-bold text-lg mb-1">Location To Be Determined</h4>
              <p className="text-xs text-white/80">
                We&apos;re still picking our spot. The interactive map appears here automatically
                once the address is set.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  </section>
  );
};
